import type { Pos, Direction, Cell, WallSet, Grid, Token, TokenColor, TeleporterPair } from "../types.ts";

export function posToKey(pos: Pos): string {
  return `${pos.row},${pos.col}`;
}

export function keyToPos(key: string): Pos {
  const [row, col] = key.split(",").map(Number);
  return { row: row!, col: col! };
}

export function createEmptyGrid(rows: number, cols: number): Grid {
  const cells: Cell[][] = [];
  for (let r = 0; r < rows; r++) {
    const row: Cell[] = [];
    for (let c = 0; c < cols; c++) {
      row.push({
        pos: { row: r, col: c },
        walls: {
          north: r === 0,
          south: r === rows - 1,
          east: c === cols - 1,
          west: c === 0,
        },
        isHole: false,
        goalColor: null,
        teleporterId: null,
      });
    }
    cells.push(row);
  }
  return { rows, cols, cells, teleporters: [] };
}

export function isInBounds(grid: Grid, pos: Pos): boolean {
  return pos.row >= 0 && pos.row < grid.rows && pos.col >= 0 && pos.col < grid.cols;
}

export function getCell(grid: Grid, pos: Pos): Cell | undefined {
  return grid.cells[pos.row]?.[pos.col];
}

export function hasWall(grid: Grid, pos: Pos, dir: Direction): boolean {
  const cell = getCell(grid, pos);
  if (!cell) return true;
  switch (dir) {
    case "up": return cell.walls.north;
    case "down": return cell.walls.south;
    case "left": return cell.walls.west;
    case "right": return cell.walls.east;
  }
}

export function oppositeDir(dir: Direction): Direction {
  switch (dir) {
    case "up": return "down";
    case "down": return "up";
    case "left": return "right";
    case "right": return "left";
  }
}

export function getAdjacentPos(pos: Pos, dir: Direction): Pos {
  switch (dir) {
    case "up": return { row: pos.row - 1, col: pos.col };
    case "down": return { row: pos.row + 1, col: pos.col };
    case "left": return { row: pos.row, col: pos.col - 1 };
    case "right": return { row: pos.row, col: pos.col + 1 };
  }
}

export function addWallBetween(grid: Grid, a: Pos, b: Pos): void {
  const dr = b.row - a.row;
  const dc = b.col - a.col;

  const cellA = getCell(grid, a);
  const cellB = getCell(grid, b);
  if (!cellA || !cellB) return;

  if (dr === 1 && dc === 0) {
    cellA.walls.south = true;
    cellB.walls.north = true;
  } else if (dr === -1 && dc === 0) {
    cellA.walls.north = true;
    cellB.walls.south = true;
  } else if (dr === 0 && dc === 1) {
    cellA.walls.east = true;
    cellB.walls.west = true;
  } else if (dr === 0 && dc === -1) {
    cellA.walls.west = true;
    cellB.walls.east = true;
  }
}

export function cloneTokens(tokens: Token[]): Token[] {
  return tokens.map((t) => ({ ...t, pos: { ...t.pos } }));
}

export function tokensEqual(a: Token[], b: Token[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const ta = a[i]!;
    const tb = b[i]!;
    if (ta.pos.row !== tb.pos.row || ta.pos.col !== tb.pos.col || ta.lost !== tb.lost) {
      return false;
    }
  }
  return true;
}

export function checkWin(grid: Grid, tokens: Token[]): boolean {
  for (const token of tokens) {
    if (token.lost) return false;
    const cell = getCell(grid, token.pos);
    if (!cell || cell.goalColor !== token.color) return false;
  }
  return true;
}
